import { useState } from 'react';
import { useSearchParamsWithApiKey, useDebouncedEffect } from '../../tools';
import type { Physicality } from './types';

interface UseResizeParams {
    document: string;
    physicalities: Physicality[];
    surfaceSpecificationUrl: string;
    surfaceSpecification: any;
    sourceSurfaceSpecificationUrl: string;
    sourceSurfaceSpecification: any;
    customPhysicality?: Physicality;
    debug: boolean;
    useAlpha: boolean;
    minimumFontSize: string;
    priority: string;
    setDocument: (document: string) => void;
    setPhysicalities: (physicalities: Physicality[]) => void;
    setSurfaceSpecificationUrl: (url: string) => void;
    setSurfaceSpecification: (surfaceSpec: any) => void;
    setSourceSurfaceSpecificationUrl: (url: string) => void;
    setSourceSurfaceSpecification: (surfaceSpec: any) => void;
    setCustomPhysicality: (physicality: Physicality | undefined) => void;
    setDebug: (debug: boolean) => void;
    setUseAlpha: (useAlpha: boolean) => false | void;
    setMinimumFontSize: (minimumFontSize: string) => void;
    setPriority: (priority: string) => void;
}

export const useResizeParams = (): UseResizeParams => {
    const [searchParams, setSearchParams] = useSearchParamsWithApiKey();
    const physicalitiesParam = searchParams.get('physicalities');
    const customPhysicalitiesParam = searchParams.get('customphysicalities');

    const documentUrl = searchParams.get('documentUrl');
    const physicalitiesJson = physicalitiesParam ? JSON.parse(physicalitiesParam) : [];
    const customPhysicalitiesJson = customPhysicalitiesParam ? JSON.parse(customPhysicalitiesParam) : '';
    const surfaceSpecificationParam = searchParams.get('surfaceSpecification');
    const surfaceSpecificationUrlParam = searchParams.get('surfaceSpecificationUrl');
    const sourceSurfaceSpecificationParam = searchParams.get('sourceSurfaceSpecification');
    const sourceSurfaceSpecificationUrlParam = searchParams.get('sourceSurfaceSpecificationUrl');
    const debugMode = searchParams.get('debug');
    const useAlphaParam = searchParams.get('useAlpha');
    const minimumFontSizeParam = searchParams.get('minimumFontSize');
    const prioritySearchParam = searchParams.get('priority');

    const [document, setDocument] = useState(documentUrl || '');
    const [physicalities, setPhysicalities] = useState<Physicality[]>(physicalitiesJson || []);
    const [customPhysicality, setCustomPhysicality] = useState<Physicality | undefined>(customPhysicalitiesJson);
    const [surfaceSpecificationUrl, setSurfaceSpecificationUrl] = useState(surfaceSpecificationUrlParam || '');
    const [surfaceSpecification, setSurfaceSpecification] = useState(
        surfaceSpecificationParam ? JSON.parse(surfaceSpecificationParam) : undefined,
    );
    const [sourceSurfaceSpecificationUrl, setSourceSurfaceSpecificationUrl] = useState(
        sourceSurfaceSpecificationUrlParam || '',
    );
    const [sourceSurfaceSpecification, setSourceSurfaceSpecification] = useState(
        sourceSurfaceSpecificationParam ? JSON.parse(sourceSurfaceSpecificationParam) : undefined,
    );
    const [debug, setDebug] = useState(debugMode === 'true');
    const [useAlpha, setUseAlpha] = useState(useAlphaParam === 'true');
    const [minimumFontSize, setMinimumFontSize] = useState(minimumFontSizeParam || '');
    const [priority, setPriority] = useState(prioritySearchParam || 'quality');

    useDebouncedEffect(
        () => {
            const searchParamsObj: Record<string, string> = {};

            if (document) {
                searchParamsObj['documentUrl'] = document;
            }

            if (physicalities.length > 0) {
                searchParamsObj['physicalities'] = JSON.stringify(physicalities);
            }

            if (surfaceSpecificationUrl) {
                searchParamsObj['surfaceSpecificationUrl'] = surfaceSpecificationUrl;
            }

            if (surfaceSpecification) {
                searchParamsObj['surfaceSpecification'] = JSON.stringify(surfaceSpecification);
            }

            if (customPhysicality) {
                searchParamsObj['customphysicalities'] = JSON.stringify(customPhysicality);
            }

            if (minimumFontSize) {
                searchParamsObj['minimumFontSize'] = minimumFontSize;
            }

            if (priority) {
                searchParamsObj['priority'] = priority.toString();
            }

            if (useAlpha) {
                searchParamsObj['useAlpha'] = 'true';
            }

            if (sourceSurfaceSpecificationUrl) {
                searchParamsObj['sourceSurfaceSpecificationUrl'] = sourceSurfaceSpecificationUrl;
            }

            if (sourceSurfaceSpecification) {
                searchParamsObj['sourceSurfaceSpecification'] = JSON.stringify(sourceSurfaceSpecification);
            }

            setSearchParams(searchParamsObj);
        },
        [
            document,
            physicalities,
            surfaceSpecificationUrl,
            surfaceSpecification,
            sourceSurfaceSpecificationUrl,
            sourceSurfaceSpecification,
            customPhysicality,
            minimumFontSize,
            priority,
            setSearchParams,
            useAlpha,
        ],
        500,
    );

    return {
        document,
        physicalities,
        surfaceSpecificationUrl,
        surfaceSpecification,
        sourceSurfaceSpecificationUrl,
        sourceSurfaceSpecification,
        customPhysicality,
        debug,
        useAlpha,
        minimumFontSize,
        priority,
        setDocument,
        setPhysicalities,
        setSurfaceSpecificationUrl,
        setSurfaceSpecification,
        setSourceSurfaceSpecificationUrl,
        setSourceSurfaceSpecification,
        setCustomPhysicality,
        setDebug,
        setUseAlpha,
        setMinimumFontSize,
        setPriority,
    };
};
